import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import dynamic from 'next/dynamic';
import styled from 'styled-components';

import { getIntervals } from '../../utils/chart';
import zIndexScale from '../../utils/zIndexScale';
import { track, sensorEventTypes } from '../../utils/sensors';

import FlexBox from '../FlexBox';
import Dropdown from '../Dropdown';
import IndicatorPanel from './IndicatorPanel';
import { HoverPanelItem } from './HoverPanelItem';
import withI18n from '../../utils/withI18n';
import { getLabel } from '../../utils/i18n';

import {
  setChartIndicator as setChartIndicatorAction,
  setDataIsLoading as setDataIsLoadingAction,
} from '../../actions/load';

import TradingViewChartRenderer, { TRADINGVIEW_VIEW_NAME } from '../Chart/TradingViewChartRenderer';
import { socketReceivedBatch as socketReceivedBatchAction } from '../../actions/websocket';

const DEFAULT_VIEW_NAME = 'default';
const DEPTH_VIEW_NAME = 'depth';
const { CHART_CHANGE } = sensorEventTypes;

const WS_PATH = '/stream?streams=';

// do not SSR standard charts
const DefaultChartRenderer = dynamic(import('./DefaultChartRenderer'), { ssr: false, loading: () => null });

const DepthChartRenderer = dynamic(import('./DepthChartRenderer'), { ssr: false, loading: () => null });

const AbstractSocketProvider = dynamic(import('../../network/AbstractSocketProvider'), { ssr: false, loading: () => null });

const CHART_INTERVALS = getIntervals();

const DEFAULT_INTERVAL_NAME = '1h';
const DEFAULT_INTERVAL = CHART_INTERVALS[DEFAULT_INTERVAL_NAME];

const DEFAULT_CHART_LINE_TYPE = 'Candle';

const CHART_INTERVALS_INV = {};
Object.keys(CHART_INTERVALS).forEach(k => { CHART_INTERVALS_INV[CHART_INTERVALS[k]] = k; });

const ChartViewport = styled.div`
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.bg};
  position: ${({ fullScreen }) =>
    fullScreen ? 'fixed' : 'relative'};
  z-index: ${({ fullScreen }) =>
    fullScreen ? zIndexScale.fullScreen : ''};
  overflow: hidden;
  height: 100%;
  width: 100%;
  left: 0;
  top: 0;
`;

const Panel = styled(FlexBox)`
  justify-content: space-between;
  background: ${({theme})=>theme.boxheadbg};
`;

const TimeList = styled.ul`
  display: ${({ selectedView }) => selectedView !== DEPTH_VIEW_NAME ? 'flex;' : 'none;'}
  flex-wrap: wrap;
  list-style: none;
  margin: 0;
  padding: 0;
  position: absolute;
  left: 0;
  top: 2em;
  z-index: 110;
  white-space: nowrap;
  box-shadow: 1px 1px 10px rgba(0,0,0,0.35);
`;

const TimeListItem = styled.li`
  color: ${({ theme, active }) => active ? theme.chartButtonColorActive : theme.fontColor};
  cursor: pointer;
  display: flex;
  flex: 1 1 100%;
  width: 100%;
  box-shadow: rgba(0,0,0,0.35);;
  border-top: 1px solid ${({ theme }) => theme.border};
  border-left: 1px solid ${({ theme }) => theme.border};
  border-right: 1px solid ${({ theme }) => theme.border};
  button {
    background-color: ${({ theme }) => theme.bg};
    color:${({theme})=>theme.fontColor};
    border: 0;
    cursor: pointer;
    font-weight: ${({ active }) => active ? '500' : ''};
    padding: .3em 1.2em;
    width: 100%;
    flex: 0 0 100%;

    &:focus {
      outline: none;
    }

    &:hover {
      background-color: ${({ theme }) => theme.chartbuttonbg};
      color: ${({ theme }) => theme.highlight};
    }
  }

  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.border};
  }
`;

const LeftPanel = styled.ul`

  display:  ${({ hidden }) => hidden ? 'none' : 'flex'};
  list-style: none;
  margin: 0;
  padding: 0;
`;

const RightPanel = styled(LeftPanel)`
  margin-left: auto;
`;

const PanelItem = styled.li`
  align-items: center;
  cursor: pointer;
  color: ${({ theme, isSet }) => isSet ? theme.chartButtonColorActive || '' : ''};
  background-color: ${({ theme, isSet }) => isSet ? theme.buttonbgactive : theme.chartbuttonbg};
  border-width: 1px;
  border-color: ${({ theme}) => theme.chartButtonBorderColor || ''};
  display:  ${({ hidden }) => hidden ? 'none' : 'flex'};
  margin: 1px;
  padding: ${({ hasDropdown }) => hasDropdown ? '0 1em 0 .4em' : '0 .4em'};
  position: relative;
  user-select: none;
  ${({ hasDropdown }) => hasDropdown ? `
    min-width: 40px; /* meh prevents some moving around as content changes */
    &:after {
      content: '▾';
      right: .2em;
      position: absolute;
      transform: rotate(0);
      transition: transform .3s;
    }
  ` : ''}
  ${({ hasDropdown, hide }) => hasDropdown && !hide ? `
    &:after {
      transform: rotate(180deg);
    }
  ` : ''}
`;

const FullScreenButton = styled.button`
  background: rgba(0,0,0,0);
  border: none;
  color: ${({ theme }) => theme.fontColor};
  cursor: pointer;
  line-height: 1.15;

  &:focus {
    outline: none;
  }
`;

const Wrapper = styled.div`
  background: ${({ theme }) => theme.boxbg};
  display: flex;
  flex-direction: column;
  position: relative;
  width: 100%;
  height: 80%;
  flex: 1 1 440px;
`;
const replace=(name,endWith,i18nName)=>{
  return name.endsWith(endWith) ? name.replace(endWith,i18nName) : i18nName
}
const TimeDropdown = (props) => (
  <Dropdown
    component={TimeList}
    keys={props.keys}
    isOpen={!props.hide}
    {...props}
  >
    {({ key }) => (
      <TimeListItem key={key} active={key === props.intervalName}>
        <button
          onClick={() => { props.onClickHandler({ key: CHART_INTERVALS[key] }); }}
          value={key}
        >
          {key.endsWith('m')?replace(key,'m',getLabel('exchange-trade-minute',props.i18n)):replace(key,'h',getLabel('exchange-trade-hour',props.i18n))}
        </button>
      </TimeListItem>
    )}
  </Dropdown>
);

TimeDropdown.propTypes = {
  keys: PropTypes.array,
  hide: PropTypes.bool.isRequired,
  onClickHandler: PropTypes.func.isRequired,
  intervalName: PropTypes.string,
  i18n: PropTypes.object,
};

TimeDropdown.defaultProps = {
  keys: [],
  intervalName: '1m',
  i18n:{},
};

const mapStateToProps = ({
  info, books, klineUpdates, isDataLoading, curIndicators,
}, { symbol, lastKlineUpdate }) => {
  const { symbols } = info;
  if (!symbols) return;
  const symbolInfo = symbols.find(({ symbol: symb }) => symbol === symb);
  // do not cause error on inactive market.
  try {
    const symbolFilter = symbolInfo.filters.find((filter) => filter.stepSize || filter.stepSize) ;
    const tickSize = symbolFilter.stepSize || symbolFilter.tickSize;
    const { minQty } = symbolInfo.filters.find((filter) => filter.minQty);
    const props = {
      symbols,
      symbolInfo,
      tickSize,
      minQty,
      isDataLoading: isDataLoading.kline,
      curIndicators,
    };
    if (klineUpdates[symbol] && klineUpdates[symbol] !== lastKlineUpdate) {
      return { ...props, lastKlineUpdate: klineUpdates[symbol] };
    }
    return props;
  } catch (err) {
    return {
      symbols, isDataLoading: false, curIndicators,
    };
  }
};

const Indicators = [
  { display: 'MACD', value: 'MACD' },
  { display: 'TRIX', value: 'TRIX' },
  { display: 'KDJ', value: 'KDJ' },
  { display: 'StochRSI', value: 'StochRSI' },
  { display: 'RSI', value: 'RSI' },
  { display: 'EMV', value: 'EMV' },
  { display: 'DMI', value: 'DMI' },
  { display: 'WR', value: 'WR' },
  { display: 'OBV', value: 'OBV' },
  { display: 'BOLL', value: 'BOLL' },
  { display: 'MTM', value: 'MTM' },
  { display: 'SAR', value: 'SAR' },
  { display: 'EMA', value: 'EMA' },
  { display: 'CCI', value: 'CCI' },
  { display: 'VWAP', value: 'VWAP' },
  { display: 'MA', value: 'MA' },
  { display: 'AVL', value: 'AVL' },
  { display: 'VOL', value: 'VOL' },
];

const Draws = [
  { display: 'tradePro-chart-line', value: 'lineSegment' },
  { display: 'tradePro-chart-yaLine', value: 'singleLine' },
  { display: 'tradePro-chart-regressionLine', value: 'regressionLine' },
  { display: 'tradePro-chart-rectangle', value: 'rectLine' },
  { display: 'tradePro-chart-cycleLine', value: 'periodicLine' },
  { display: 'tradePro-chart-goldenSection', value: 'godPercentLine' },
  { display: 'tradePro-chart-percentLine', value: 'percentLine' },
  { display: 'tradePro-chart-bandLine', value: 'wavePercentLines' },
  { display: 'tradePro-chart-peiBoLine', value: 'fibonacciSequence' },
  { display: 'tradePro-chart-upArrow', value: 'upArrow' },
  { display: 'tradePro-chart-downArrow', value: 'downArrow' },
  { display: 'tradePro-chart-deleteSelected', value: 'deleteSelected' },
  { display: 'tradePro-chart-clear', value: 'clear' },

];

export default
@connect(mapStateToProps,
  { setCurIndicator: setChartIndicatorAction, setDataIsLoading: setDataIsLoadingAction,socketReceivedBatch:socketReceivedBatchAction })
class ChartComponent extends Component {
  static propTypes = {
    lastKlineUpdate: PropTypes.object,
    locale: PropTypes.string,
    minQty: PropTypes.string,
    symbol: PropTypes.string,
    symbolInfo: PropTypes.object,
    symbols: PropTypes.array,
    tickSize: PropTypes.string,
    wssUri: PropTypes.string.isRequired,
    i18n: PropTypes.object,
    isDataLoading: PropTypes.bool,
    curIndicators: PropTypes.object,
    socketReceivedBatch: PropTypes.func,
    showInds: PropTypes.bool,
    setCurIndicator: PropTypes.func,
    setDataIsLoading: PropTypes.func,
  };

  static defaultProps = {
    lastKlineUpdate: null,
    locale: 'en',
    minQty: '0.01',
    symbol: '',
    symbolInfo: {},
    symbols: [],
    tickSize: '0.01',
    i18n: {},
    isDataLoading: false,
    curIndicators: {},
    showInds: false,
    setCurIndicator: () => {},
    setDataIsLoading: () => {},
    socketReceivedBatch: ()=>{}
  }

  state = {
    fullScreen: false,
    interval: DEFAULT_INTERVAL,
    lineType: DEFAULT_CHART_LINE_TYPE,
    intervalName: DEFAULT_INTERVAL_NAME,
    selectedView: DEFAULT_VIEW_NAME,
    hMenuPopped: false,
    mMenuPopped: false,
    dataSetup: 0,
  }

  componentDidMount() {
    const localSelectedView = window.localStorage.getItem('bn.selectedView');
    const localInterval = window.localStorage.getItem('bn.interval');
    const localIntervalName = window.localStorage.getItem('bn.intervalName');
    const localLineType = window.localStorage.getItem('bn.lineType');
    if (localLineType ==='Line') {
      this.setState({lineType: localLineType});
      this._setInterval({ key: CHART_INTERVALS['1m'], type: 'Line' });
    }
    if (localSelectedView) this._setSelectedView(localSelectedView);
    if (localInterval && localIntervalName) {
      this._setSelectedInterval(parseInt(localInterval, 10), localIntervalName);
    }
  }

  _onSocketReconnect = () => {
    // refresh chart data
    this.props.setDataIsLoading({ key: 'kline', isLoading: true });
    this.setState({ dataSetup: this.state.dataSetup + 1 });
    if (__DEV__) console.log('===_onSocketReconnect = true');
  };

  _setInterval = (ev) => {
    if (!ev || !ev.key) return;

    const type = ev.type || 'Candle';
    const value = ev.key;
    const interval = parseInt(value, 10);
    const intervalName = CHART_INTERVALS_INV[value];
    this.setState({ interval, intervalName, lineType: type }, () => {
      if (!window.localStorage) return;
      window.localStorage.setItem('bn.lineType', type);
      window.localStorage.setItem('bn.interval', interval);
      window.localStorage.setItem('bn.intervalName', intervalName);
    });
  };

  _setSelectedView(selectedView) {
    this.setState({ selectedView });
  }

  _setSelectedInterval(interval, intervalName) {
    this.setState({ interval, intervalName });
  }

  _changeView = (event, newView) => {
    event.preventDefault();
    this.setState({
      selectedView: newView,
    }, () => {
      window.localStorage.setItem('bn.selectedView', newView);
    });
    track(CHART_CHANGE, {
      target: newView,
    });
  };

  _toggleFullScreen = (event) => {
    event.preventDefault();
    this.setState(({ fullScreen }) => ({ fullScreen: !fullScreen }));
  }

  _toggleHourMenu() {
    this.setState({
      hMenuPopped: !this.state.hMenuPopped,
      mMenuPopped: false,
    });
  }

  _toggleMinuteMenu() {
    this.setState({
      hMenuPopped: false,
      mMenuPopped: !this.state.mMenuPopped,
    });
  }

  _setIndicator(indicatorName) {
    this.setState({ IndiName: indicatorName });
    this.setState({ IndicatorShow: false });
  }

  _setDraws(type) {
    this.setState({ drawLineType: type });
    this.setState({ DrawsShow: false });
  }

  _onMessage=(data)=>{
    const {socketReceivedBatch}= this.props
    socketReceivedBatch([data])
  }

  render() {
    const {
      symbol, i18n, locale, wssUri, showInds,
    } = this.props;
    const {
      fullScreen,
      interval,
      lineType,
      intervalName,
      hMenuPopped,
      mMenuPopped,
      IndiName,
      drawLineType,
      selectedView,
    } = this.state;
    const klinesStreamName = `${symbol.toLowerCase()}@kline_${intervalName}`;
    const streamUrl = klinesStreamName?wssUri.replace(/\/ws$/, WS_PATH)+klinesStreamName:'';
    return (
      <ChartViewport fullScreen={fullScreen}>
          <AbstractSocketProvider
            url={streamUrl}
            keepAlive={true}
            onReconnect={this._onSocketReconnect}
            onMessage={this._onMessage}
          />
        <form>
          <Panel bg="theme" justify="space-between" role="menu">
          {selectedView === DEPTH_VIEW_NAME?null:
            (<LeftPanel>
              <PanelItem
                role="menuitem"
                onClick={() => { this._setInterval({ key: CHART_INTERVALS['1m'], type: 'Line' }); }}
                onKeyDown={() => { this._setInterval({ key: CHART_INTERVALS['1m'], type: 'Line' }); }}
                isSet={intervalName === '1m'&& lineType === 'Line'}
              >
                {getLabel('exchange-trade-Line', i18n)}
              </PanelItem>
              <PanelItem
                role="menu"
                aria-expanded={mMenuPopped}
                onClick={() => this._toggleMinuteMenu()}
                onKeyDown={() => this._toggleMinuteMenu()}
                hasDropdown="true"
                hide={!mMenuPopped}
                isSet={intervalName.endsWith('m')&& lineType !== 'Line'}
              >
                {replace(intervalName,'m',getLabel('exchange-trade-minute',i18n)) || 'Min'}
                <TimeDropdown
                  hide={!mMenuPopped}
                  i18n={i18n}
                  keys={Object.keys(CHART_INTERVALS).filter(_interval => _interval.endsWith('m'))}
                  onClickHandler={this._setInterval}
                  intervalName={intervalName}
                />
              </PanelItem>
              <PanelItem
                role="menu"
                aria-expanded={hMenuPopped}
                onClick={() => this._toggleHourMenu()}
                onKeyDown={() => this._toggleHourMenu()}
                hasDropdown="true"
                hide={!hMenuPopped}
                isSet={intervalName.endsWith('h')}
              >
                {replace(intervalName,'h',getLabel('exchange-trade-hour',i18n)) || 'Hour'}
                <TimeDropdown
                  hide={!hMenuPopped}
                  i18n={i18n}
                  keys={Object.keys(CHART_INTERVALS).filter(_interval => _interval.endsWith('h'))}
                  onClickHandler={this._setInterval}
                  intervalName={intervalName}
                />
              </PanelItem>
              <PanelItem
                role="menuitem"
                onClick={() => { this._setInterval({ key: CHART_INTERVALS['1d'] }); }}
                onKeyDown={() => { this._setInterval({ key: CHART_INTERVALS['1d'] }); }}
                isSet={intervalName === '1d'}
              >
                {getLabel('exchange-trade-dayLine', i18n)}
              </PanelItem>
              <PanelItem
                role="menuitem"
                onClick={() => { this._setInterval({ key: CHART_INTERVALS['1w'] }); }}
                onKeyDown={() => { this._setInterval({ key: CHART_INTERVALS['1w'] }); }}
                isSet={intervalName === '1w'}
              >
                {getLabel('exchange-trade-weekLine', i18n)}
              </PanelItem>
              <PanelItem
                role="menuitem"
                onClick={() => { this._setInterval({ key: CHART_INTERVALS['1M'] }); }}
                onKeyDown={() => { this._setInterval({ key: CHART_INTERVALS['1M'] }); }}
                isSet={intervalName === '1M'}
              >
                {getLabel('exchange-trade-mouthLine', i18n)}
              </PanelItem>
              {selectedView!==TRADINGVIEW_VIEW_NAME?
              <PanelItem
                role="menuitem"
                hidden={!showInds}
                onClick={() => {
                  this.setState({ DrawsShow: !this.state.DrawsShow, IndicatorShow: false });
                }}
                onKeyDown={() => {
                  this.setState({ DrawsShow: !this.state.DrawsShow, IndicatorShow: false });
                }}
                isSet={this.state.DrawsShow}
              >
                {getLabel('exchange-trade-draw', i18n)}
                <IndicatorPanel
                  indicators={Draws}
                  hidden={!this.state.DrawsShow}
                  onRowClick={ev => this._setDraws(ev)}
                  i18n={i18n}
                />
              </PanelItem>
                :null}

              <PanelItem
                role="menuitem"
                hidden={!showInds}
                onClick={() => {
                  if(selectedView === TRADINGVIEW_VIEW_NAME)
                    this.setState({ IndicatorShowTV: !this.state.IndicatorShowTV});
                  else
                    this.setState({ IndicatorShow: !this.state.IndicatorShow, DrawsShow: false });
                }}
                onKeyDown={() => {
                   if(selectedView === TRADINGVIEW_VIEW_NAME)
                    this.setState({ IndicatorShowTV: !this.state.IndicatorShowTV});
                  else
                    this.setState({ IndicatorShow: !this.state.IndicatorShow, DrawsShow: false });
                }}
                isSet={selectedView !== TRADINGVIEW_VIEW_NAME && this.state.IndicatorShow}
              >
                {getLabel('exchange-trade-technicalIndex', i18n)}
                { selectedView === TRADINGVIEW_VIEW_NAME ? null : (
                  <IndicatorPanel
                    indicators={Indicators}
                    hidden={!this.state.IndicatorShow}
                    onRowClick={(evet) => this._setIndicator(evet)}
                  />
                )}
              </PanelItem>
            </LeftPanel>)}
            { selectedView === TRADINGVIEW_VIEW_NAME ? null :
            <LeftPanel hidden={!showInds||selectedView !== DEFAULT_VIEW_NAME}>
              <HoverPanelItem hidden={!this.props.curIndicators.curInd}>
                {this.props.curIndicators.curInd}
                <i onClick={() => { this.props.setCurIndicator({ curInd: null }); }} role="menuitem" tabIndex="0" onKeyDown={() => { this.props.setCurIndicator({ curInd: null }); }} >X</i>
              </HoverPanelItem>
              <HoverPanelItem hidden={!this.props.curIndicators.curOver}>
                {this.props.curIndicators.curOver}
                <i onClick={() => { this.props.setCurIndicator({ curOver: null }); }} role="menuitem" tabIndex="0" onKeyDown={() => { this.props.setCurIndicator({ curOver: null }); }}>X</i>
              </HoverPanelItem>
            </LeftPanel> }
            <RightPanel>
              <PanelItem
                role="menuitem"
                onClick={(ev) => { this._changeView(ev, DEFAULT_VIEW_NAME); }}
                onKeyDown={(ev) => { this._changeView(ev, DEFAULT_VIEW_NAME); }}
                isSet={selectedView === DEFAULT_VIEW_NAME}
              >
                {getLabel('exchange-trade-Original', i18n)}
              </PanelItem>
              <PanelItem
                role="menuitem"
                onClick={(ev) => { this._changeView(ev, TRADINGVIEW_VIEW_NAME); }}
                onKeyDown={(ev) => { this._changeView(ev, TRADINGVIEW_VIEW_NAME); }}
                isSet={selectedView === TRADINGVIEW_VIEW_NAME}
              >
                {getLabel('exchange-trade-TradingView', i18n)}
              </PanelItem>
              <PanelItem
                role="menuitem"
                onClick={(ev) => { this._changeView(ev, DEPTH_VIEW_NAME); }}
                onKeyDown={(ev) => { this._changeView(ev, DEPTH_VIEW_NAME); }}
                isSet={selectedView === DEPTH_VIEW_NAME}
              >
                {getLabel('exchange-trade-Depth', i18n)}
              </PanelItem>
              <PanelItem>
                <FullScreenButton
                  onClick={this._toggleFullScreen}
                  aria-label="full screen"
                >
                  <i className={`iconfont ${fullScreen ? 'icon-offfull' : 'icon-full'}`} />
                </FullScreenButton>
              </PanelItem>
            </RightPanel>
          </Panel>
        </form>
        <Wrapper>
          <DepthChartRenderer
            hidden={selectedView !== DEPTH_VIEW_NAME}
            selectedView={selectedView}
            fullScreen={fullScreen}
            symbol={symbol}
          />
          <DefaultChartRenderer
            hidden={selectedView !== DEFAULT_VIEW_NAME}
            fullScreen={fullScreen}
            selectedView={selectedView}
            locale={locale}
            symbol={symbol}
            interval={interval}
            intervalName={intervalName}
            lineType={lineType}
            indicatorName={IndiName}
            drawLineType={drawLineType}
            dataSetup={this.state.dataSetup}
            pro={this.props.pro}
          />
          <TradingViewChartRenderer
            hidden={selectedView !== TRADINGVIEW_VIEW_NAME}
            fullScreen={fullScreen}
            selectedView={selectedView}
            showIndicatorsDialog={this.state.IndicatorShowTV}
            locale={locale}
            symbol={symbol}
            interval={interval}
            lineType={lineType}
          />
        </Wrapper>
      </ChartViewport>);
  }
}
